/* Main-thread side of the Python runner (see public/pyodide.worker.js for the protocol).
 *
 * The worker cannot interrupt its own Python code, so infinite loops are handled here: if a run
 * takes longer than `timeoutMs` once the runtime is ready, the worker is terminated, the run
 * resolves with status "timeout", and a fresh worker boots for the next run.
 */
import { useSyncExternalStore } from "react";

export const RUN_TIMEOUT_MS = 15000;

export function createPythonRunner({
  workerUrl,
  timeoutMs = RUN_TIMEOUT_MS,
  createWorker = (url) => new Worker(url),
} = {}) {
  let worker = null;
  let nextId = 1;
  const pending = new Map(); // id -> { resolve, timer }
  const listeners = new Set();
  let state = { status: "idle", pythonVersion: null, message: null };

  function setState(patch) {
    state = { ...state, ...patch };
    listeners.forEach((listener) => listener());
  }

  function startTimer(id) {
    const entry = pending.get(id);
    if (!entry || entry.timer) return;
    entry.timer = setTimeout(() => {
      finish(id, {
        status: "timeout",
        stdout: "",
        stderr: "",
        error: null,
        tests: [],
        durationMs: timeoutMs,
      });
      restart();
    }, timeoutMs);
  }

  function finish(id, result) {
    const entry = pending.get(id);
    if (!entry) return;
    clearTimeout(entry.timer);
    pending.delete(id);
    entry.resolve(result);
  }

  function failAll(message) {
    for (const id of [...pending.keys()]) {
      finish(id, { status: "error", stdout: "", stderr: "", error: message, tests: [], durationMs: 0 });
    }
  }

  function handleMessage(event) {
    const data = event.data || {};
    if (data.type === "status") {
      setState({
        status: data.status,
        pythonVersion: data.pythonVersion || state.pythonVersion,
        message: data.message || null,
      });
      if (data.status === "ready") pending.forEach((_, id) => startTimer(id));
      if (data.status === "failed") failAll(data.message || "Python runtime failed to load.");
    } else if (data.type === "result") {
      finish(data.id, data.result);
    }
  }

  function start() {
    if (worker) return;
    setState({ status: "loading", message: null });
    worker = createWorker(workerUrl);
    worker.onmessage = handleMessage;
    worker.onerror = (event) => {
      const message = (event && event.message) || "Python runtime crashed.";
      setState({ status: "failed", message });
      failAll(message);
    };
  }

  function stop() {
    if (worker) worker.terminate();
    worker = null;
  }

  function restart() {
    stop();
    failAll("The Python runtime was restarted.");
    start();
  }

  function run(code, tests = []) {
    start();
    const id = nextId++;
    return new Promise((resolve) => {
      pending.set(id, { resolve, timer: null });
      worker.postMessage({ id, type: "run", code, tests });
      if (state.status === "ready") startTimer(id);
    });
  }

  return {
    start,
    run,
    restart,
    dispose() {
      stop();
      failAll("The Python runtime was stopped.");
      setState({ status: "idle" });
    },
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

let sharedRunner = null;

/** The single runner used by the app; the worker boots on first use and is kept between levels. */
export function getPythonRunner() {
  if (!sharedRunner) {
    sharedRunner = createPythonRunner({ workerUrl: `${process.env.PUBLIC_URL}/pyodide.worker.js` });
  }
  return sharedRunner;
}

export function usePythonRunner(runner = getPythonRunner()) {
  const state = useSyncExternalStore(runner.subscribe, runner.getState, runner.getState);
  return { runner, ...state };
}
